import {
  Alert,
  Box,
  Checkbox,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { FormEvent, useState } from 'react';
import { allClassNumbers } from '../../helpers/academic-years';
import { Category, YearSegment } from '../../types/category';
import { Subject } from '../../types/subject';

export interface CategoryWithYearSegments {
  category: Category;
  yearSegments?: Record<number, YearSegment>;
}

interface CategoryEditDialogProps {
  onClose: () => void;
  onConfirm: (
    updatedCategory: Category,
    yearSegments: Record<number, YearSegment>
  ) => void;
  categoryWithYearSegments: CategoryWithYearSegments | null;
  selectedSubject: Subject | null;
}

const CategoryEditDialog: React.FC<CategoryEditDialogProps> = ({
  onClose,
  onConfirm,
  categoryWithYearSegments,
  selectedSubject,
}) => {
  const [showMissingYearsError, setShowMissingYearsError] = useState(false);
  const [years, setYears] = useState<Record<number, YearSegment>>(
    categoryWithYearSegments?.yearSegments ?? {}
  );

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (selectedSubject && Object.keys(years).length === 0) {
      setShowMissingYearsError(true);
      return;
    }

    const formData = new FormData(event.currentTarget);

    const updatedCategory: Category = {
      name: (formData.get('name') as string).trim(),
    };

    const yearSegments: Record<number, YearSegment> = years;
    updatedCategory.subjectId = selectedSubject?.id?.toString();

    if (categoryWithYearSegments) {
      updatedCategory.id = categoryWithYearSegments.category.id;
    } else {
      updatedCategory.subjectId = selectedSubject?.id?.toString();
    }

    onConfirm(updatedCategory, yearSegments);
  };

  const toggleYear = (classNumber: number) => {
    const updatedYears = { ...years };

    if (updatedYears[classNumber] === undefined) {
      updatedYears[classNumber] = YearSegment.WholeYear;
    } else {
      delete updatedYears[classNumber];
    }

    setYears(updatedYears);
  };

  const getTitle = () => {
    const firstPart = categoryWithYearSegments ? 'Upravit' : 'Vytvořit novou';

    return `${firstPart} ${
      selectedSubject === null
        ? 'univerzální kategorii'
        : `kategorii pro předmět ${selectedSubject.name}`
    }`;
  };

  return (
    <Dialog
      open
      onClose={onClose}
      disableRestoreFocus
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit,
      }}
    >
      <DialogTitle>{getTitle()}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          required
          margin="dense"
          id="name"
          name="name"
          label="Název kategorie"
          type="text"
          fullWidth
          variant="standard"
          defaultValue={categoryWithYearSegments?.category?.name}
          inputProps={{
            pattern: '\\s*\\S+.*',
            title: 'Název kategorie nesmí být prázdný',
          }}
        />
        {selectedSubject && (
          <>
            {showMissingYearsError && (
              <Alert severity="error">
                Vyplňte alespoň jedno probírané období
              </Alert>
            )}
            <Typography variant="h6">Probírané období</Typography>
            {allClassNumbers.map((classNumber) => (
              <Box display="flex" my={0.5} key={classNumber}>
                <Typography
                  variant="h6"
                  alignContent={'center'}
                >{`${classNumber}.`}</Typography>
                <Checkbox
                  checked={years[classNumber] !== undefined}
                  onChange={() => toggleYear(classNumber)}
                />
                <ToggleButtonGroup
                  disabled={years[classNumber] === undefined}
                  value={years[classNumber]}
                  exclusive
                  onChange={(_, yearSegment) => {
                    if (yearSegment === null) return;
                    setYears({
                      ...years,
                      [classNumber]: yearSegment,
                    });
                  }}
                  size="small"
                >
                  <ToggleButton value={YearSegment.WholeYear}>
                    <Typography noWrap>Celý rok</Typography>
                  </ToggleButton>
                  <ToggleButton value={YearSegment.FirstHalf}>
                    <Typography noWrap>1. pololetí</Typography>
                  </ToggleButton>
                  <ToggleButton value={YearSegment.SecondHalf}>
                    <Typography noWrap>2. pololetí</Typography>
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            ))}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Zrušit</Button>
        <Button variant="contained" type="submit">
          {categoryWithYearSegments ? 'Uložit' : 'Vytvořit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CategoryEditDialog;
